import { BasketballGame, BasketballGameStatus } from '@hefe/models';
import { BasketballGamesSlice, currentUserSelector } from '@hefe/store';
import { Checkbox, Col, Descriptions, Radio, Row, Spin } from 'antd';
import { t } from '@hefe/i18n';
import React, { useState } from 'react';
import { AiOutlineFilePdf } from 'react-icons/ai';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useAppDispatch } from '../../../../../store/store';
import { clubHasSubscription } from '@hefe/utils';
import { AuthSubscriptions } from '@hefe/auth';
import { useSelector } from 'react-redux';

interface GameDetailsAdminComponentProps {
  game: BasketballGame;
}

export default function GameDetailsAdminComponent(
  props: GameDetailsAdminComponentProps
) {
  const { game } = props;

  const dispatch = useAppDispatch();

  const user = useSelector(currentUserSelector);

  const [isLoadingRefereeBill, setLoadingRefereeBill] = useState(false);
  const [isSavingHomeGame, setSavingHomeGame] = useState(false);
  const [isSavingOwnReferees, setSavingOwnReferees] = useState(false);

  const onClickRefereeBill = () => {
    setLoadingRefereeBill(true);
    dispatch(BasketballGamesSlice.downloadBasketballGameRefereeBill(game.id))
      .unwrap()
      .then((response) => {
        if (response) {
          const file = new Blob([response.data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
        }
      })
      .catch(() => null)
      .finally(() => setLoadingRefereeBill(false));
  };

  const onClickHomeGame = (value: boolean | null) => {
    setSavingHomeGame(true);
    dispatch(
      BasketballGamesSlice.setBasketballGameHomeGame({
        game_id: game.id,
        is_home: value,
      })
    )
      .unwrap()
      .catch(() => null)
      .finally(() => setSavingHomeGame(false));
  };

  const onClickOwnReferees = (value: boolean) => {
    setSavingOwnReferees(true);
    dispatch(
      BasketballGamesSlice.setBasketballGameSetOwnReferees({
        game_id: game.id,
        own_referees: value,
      })
    )
      .unwrap()
      .catch(() => null)
      .finally(() => setSavingOwnReferees(false));
  };

  const renderRefereeBill = () => {
    if (isLoadingRefereeBill) {
      return <Spin size="small" />;
    }

    return (
      <AiOutlineFilePdf
        size={16}
        style={{ cursor: 'pointer' }}
        onClick={() => onClickRefereeBill()}
      />
    );
  };

  const renderHomeGame = () => {
    if (isSavingHomeGame) {
      return <Spin size="small" />;
    }

    const defaultValue =
      game.club_details?.is_home !== null
        ? game.club_details?.is_home === 1
          ? true
          : false
        : null;

    return (
      <Radio.Group
        defaultValue={defaultValue}
        onChange={(event) => onClickHomeGame(event.target.value)}
      >
        <Radio.Button value={true}>{t('home')}</Radio.Button>
        <Radio.Button value={null}>{t('auto')}</Radio.Button>
        <Radio.Button value={false}>{t('guest')}</Radio.Button>
      </Radio.Group>
    );
  };

  const renderOwnReferees = () => {
    const ownReferees = game.club_details?.own_referees;

    if (isSavingOwnReferees) {
      return <Spin size="small" />;
    }
    return (
      <Checkbox
        checked={ownReferees}
        onChange={(event) => onClickOwnReferees(event.target.checked)}
      />
    );
  };

  const renderResultStatus = () => {
    const Icon =
      game.status === BasketballGameStatus.FINISHED ? FaCheck : FaTimes;

    return <Icon />;
  };

  const renderReportSent = () => {
    const Icon = game.club_details?.score_sheet_sent ? FaCheck : FaTimes;

    return <Icon />;
  };

  return (
    <Row>
      <Col xs={24}>
        <Descriptions column={1} size="small" bordered>
          {clubHasSubscription(AuthSubscriptions.third, user) &&
            game?.club_details?.is_home_flag && (
              <Descriptions.Item label="Schiedsrichterabrechnung">
                {renderRefereeBill()}
              </Descriptions.Item>
            )}
          <Descriptions.Item label="Heimspiel">
            {renderHomeGame()}
          </Descriptions.Item>
          {game?.club_details?.is_home_flag && (
            <Descriptions.Item label="Eigene Schiedsrichter">
              {renderOwnReferees()}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Ergebnis gemeldet">
            {renderResultStatus()}
          </Descriptions.Item>
        </Descriptions>
      </Col>
    </Row>
  );
}
